import {Helmet} from '@dr.pogodin/react-helmet';
import {
  ButtonGroup,
  DataStatus,
  Dialog,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Box} from 'platform/foundation';

import {
  useGetDirectSaleVariantsQuery,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';
import {getOptionsFromActiveDirectSaleVariants} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {composePath, useNavigate} from 'shared/web';

import {
  DirectSalesVariantChangeForm,
  DirectSalesVariantChangeFormSchema,
} from './types/DirectSalesVariantChangeForm';

export function DirectSalesVariantChange() {
  const {directSaleId, directSaleVariantId} = useWarehouseParams();
  const navigate = useNavigate();

  const {
    data: directSaleVariants,
    isLoading: isDirectSaleVariantsLoading,
    isError: hasDirectSaleVariantsError,
  } = useGetDirectSaleVariantsQuery();

  const [changeDirectSaleVariant] = usePutWarehouseHeaderDynamicActionsMutation();

  const handleSubmit: FormSubmitHandler<DirectSalesVariantChangeForm> = async (formValues) => {
    await changeDirectSaleVariant({
      id: directSaleId,
      resource: 'direct-sale',
      body: {actionKey: 'variantChange', newDirectSaleVariantId: formValues.directSaleVariantId},
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('entity.warehouse.notifications.directSaleVariantChanged'));

        navigate(
          composePath(warehouseRoutes.directSalesDetailOverview, {
            params: {
              id: directSaleId,
            },
          })
        );
      })
      .catch(handleApiError);
  };

  const handleDiscard = () => {
    navigate(
      composePath(warehouseRoutes.directSalesDetailOverview, {
        params: {
          id: directSaleId,
        },
      })
    );
  };

  return (
    <>
      <Helmet title={i18n.t('page.warehouse.labels.changeDirectSaleVariant')} />
      <DataStatus
        minHeight={60}
        isLoading={isDirectSaleVariantsLoading}
        isError={hasDirectSaleVariantsError}
      >
        <Dialog
          isOpen
          title={i18n.t('page.warehouse.labels.changeDirectSaleVariant')}
          size="small"
          withAdditionalFooter
          scrollBehavior="outside"
          onClose={handleDiscard}
          data-testid={testIds.warehouse.directSalesVariantChange('dialog')}
        >
          <Form<DirectSalesVariantChangeForm>
            experimentalZodSchema={DirectSalesVariantChangeFormSchema}
            defaultValues={{directSaleVariantId}}
            onSubmit={handleSubmit}
          >
            {(control) => (
              <>
                <Box height={20}>
                  <FormField
                    isRequired
                    isNotClearable
                    control={control}
                    name="directSaleVariantId"
                    type="choice"
                    label={i18n.t('page.warehouse.labels.directSaleVariant')}
                    options={getOptionsFromActiveDirectSaleVariants(directSaleVariants)}
                    data-testid={testIds.warehouse.directSalesVariantChange('directSaleVariantId')}
                  />
                </Box>

                <DialogFooter>
                  <ButtonGroup align="right">
                    <FormButton
                      type="button"
                      title={i18n.t('general.actions.discard')}
                      variant="secondary"
                      control={control}
                      onClick={handleDiscard}
                      data-testid={testIds.warehouse.directSalesVariantChange('discard')}
                    />
                    <FormButton
                      type="submit"
                      title={i18n.t('general.actions.change')}
                      variant="primary"
                      control={control}
                      data-testid={testIds.warehouse.directSalesVariantChange('submit')}
                    />
                  </ButtonGroup>
                </DialogFooter>
              </>
            )}
          </Form>
        </Dialog>
      </DataStatus>
    </>
  );
}
